import React, { useEffect, useMemo, useState } from 'react';
import { calculateBoxNumber, esitoTrKey } from 'components/scommesseTipo';
import { useInfoAggiuntivaIndex, useInfoAggiuntivaMeta, useInfoTipo } from 'features/sport/hooks';

import { DropdownEsito } from '../components/esito';
import { KeyManagerSport } from 'features/sport';
import { ScommesseEsitoSkeleton } from 'components/scommesseEsitoSkeleton';
import { ScommesseLeafItem } from '../components/cells';
import { ScommesseTableQuotaSelect } from '../components/scommesseTableQuotaSelect';
import { isTruthy } from 'utility/functions';
import styles from './ScommesseMarcatoreEsitoOverThreeWithInfoAgg.module.scss';
import { useTranslation } from 'hooks/useLingUI';
import { SizeInfoAggiuntiva } from 'lib/api/sport/sportScommesseBySlugResponse';

type ScommesseMarcatoreEsitoOverThreeWithInfoAggProps = {
  playerKey: string;
  scommessaKey: string;
  hasHeader?: boolean;
  statePath?: Array<string>;
};

const ScommesseMarcatoreEsitoOverThreeWithInfoAgg = ({
  hasHeader,
  playerKey,
  scommessaKey,
}: ScommesseMarcatoreEsitoOverThreeWithInfoAggProps) => {
  const { t } = useTranslation();
  const infoTipoScommessa = useInfoTipo(new KeyManagerSport(scommessaKey).tipoScommessaId);

  const infoAggIndex = useInfoAggiuntivaIndex(playerKey);
  const { nextInfoAgg, defaultInfoAgg } = infoAggIndex ?? {};
  const [selectedInfoAggKey, setSelectedInfoAggKey] = useState<string | undefined>();

  const infoAggMeta = useInfoAggiuntivaMeta(playerKey);
  const { headers, headersTrKey } = infoAggMeta ?? {};

  const headerList = useMemo(() => {
    const list = (infoTipoScommessa?.headers ?? []).map((k, idx) => t(infoTipoScommessa?.headersTrKey?.[idx], k));
    list?.unshift(t(headersTrKey?.[0], headers?.[0]));
    return list;
  }, [headers, headersTrKey, infoTipoScommessa?.headers, infoTipoScommessa?.headersTrKey, t]);

  useEffect(() => {
    if (!isTruthy(nextInfoAgg?.length)) {
      setSelectedInfoAggKey(undefined);
      return;
    }

    if (!selectedInfoAggKey || !nextInfoAgg?.includes(selectedInfoAggKey)) {
      const nextSelection = defaultInfoAgg ?? nextInfoAgg?.[0];
      setSelectedInfoAggKey(nextSelection);
    }
  }, [defaultInfoAgg, nextInfoAgg, selectedInfoAggKey]);

  if (!selectedInfoAggKey || !isTruthy(nextInfoAgg?.length)) {
    return (
      <React.Fragment>
        {calculateBoxNumber(infoTipoScommessa, esitoTrKey).map(({ header, headerTrKey, size, isInfoAgg }, index) => (
          <ScommesseEsitoSkeleton
            header={header}
            headerTrKey={headerTrKey}
            key={`${header}_${index}`}
            size={isInfoAgg ? size : undefined}
            testId={headerTrKey}
          />
        ))}
      </React.Fragment>
    );
  }

  return (
    <div className={styles.groupWrapper}>
      <ScommesseTableQuotaSelect
        type={ScommesseLeafItem.MarcatoriInfoAgg}
        className={styles.select}
        headerList={headerList}
        hasBilanciata={false}
        scommessaKey={scommessaKey}
        optionsKeys={nextInfoAgg ?? []}
        infoAggiuntivaKey={selectedInfoAggKey}
        selectedOptionKey={selectedInfoAggKey}
        onOptionSelected={setSelectedInfoAggKey}
      />
      <DropdownEsito
        type="Quota"
        size={SizeInfoAggiuntiva.Large}
        headerBox={hasHeader ? '\u00a0' : undefined}
        infoAggiuntivaKey={selectedInfoAggKey}
        headerSelectTrKey={esitoTrKey}
        scommessaKey={scommessaKey}
      />
    </div>
  );
};

export default ScommesseMarcatoreEsitoOverThreeWithInfoAgg;
